import * as React from "react";
import { Box, Typography } from "@mui/material";

interface CustomSelectProps {
  value: number | string;
  onChange: (value: number | string) => void;
  options: number[] | Array<{ label: string; value: string | number }>;
  label?: string;
  minWidth?: number | string;
  labelKey?: string;
  valueKey?: string;
}

export default function CustomSelect({
  value,
  onChange,
  options,
  label,
  minWidth = 80,
  labelKey = "label",
  valueKey = "value",
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [dropUp, setDropUp] = React.useState(false);
  const selectRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const isObjectOptions = options.length > 0 && typeof options[0] === "object";

  const getDisplayValue = () => {
    if (isObjectOptions) {
      const option = (options as Array<{ label: string; value: string | number }>).find(
        (opt) => String(opt[valueKey as keyof typeof opt]) === String(value)
      );
      return option ? option[labelKey as keyof typeof option] : "";
    }
    return String(value);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (isOpen && selectRef.current && dropdownRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - selectRect.bottom;
      setDropUp(spaceBelow < dropdownHeight + 10);
    }
  }, [isOpen]);

  return (
    <Box ref={selectRef} sx={{ position: "relative", minWidth }}>
      {label && (
        <Typography variant="body2" sx={{ color: "#64748b", mb: 0.5 }}>{label}</Typography>
      )}
      <Box
        onClick={() => setIsOpen(!isOpen)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: "6px", backgroundColor: "#ffffff", cursor: "pointer", fontSize: "0.875rem", color: "#212529", "&:hover": { borderColor: "#212529" }, transition: "border-color 0.2s" }}
      >
        <Typography variant="body2">{getDisplayValue()}</Typography>
        <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "#6b7280", transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</Typography>
      </Box>
      {isOpen && (
        <Box
          ref={dropdownRef}
          sx={{ position: "absolute", left: 0, right: 0, marginTop: dropUp ? 0 : "4px", marginBottom: dropUp ? "4px" : 0, bottom: dropUp ? "100%" : "auto", top: dropUp ? "auto" : "100%", backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "6px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", zIndex: 1000, maxHeight: 200, overflowY: "auto" }}
        >
          {options.map((option, index) => {
            let optionValue: string | number;
            let optionLabel: string | number;
            if (isObjectOptions) {
              const opt = option as { label: string; value: string | number };
              optionValue = opt[valueKey as keyof typeof opt] as string | number;
              optionLabel = opt[labelKey as keyof typeof opt] as string | number;
            } else {
              optionValue = option as string | number;
              optionLabel = option as string | number;
            }
            return (
              <Box
                key={index}
                onClick={() => { onChange(optionValue); setIsOpen(false); }}
                sx={{ padding: "8px 12px", fontSize: "0.875rem", color: "#212529", cursor: "pointer", "&:hover": { backgroundColor: "#f3f4f6" } }}
              >
                {String(optionLabel)}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
